import { withErrorBoundary } from '@s4l/utils/hocs/withErrorBoundary'
import React, { useState } from 'react'
import { useS4LContext } from '@s4l/react-context'

import { IconText, SelectableOption, Typography, Tag } from '@s4l/ds'
import { IconCheck } from '@s4l/icons/IconCheck'
import { AvailabilityBasic } from '@s4l/stayforlong/booking/AvailabilityBasic'

import styles from './styles.module.css'

export interface IPaymentDateSelectorProps {
  availabilityJSON?: ReturnType<AvailabilityBasic['toJSON']>
  onClick?: (value: IPaymentDateSelectorType) => void
  isSelected?: boolean
}

export type IPaymentDateSelectorType = {
  isPayNow: boolean
  availabilityId?: string
}

function PaymentDateSelector({
  availabilityJSON,
  onClick,
  isSelected = false,
}: Readonly<IPaymentDateSelectorProps>) {
  const { i18n } = useS4LContext()
  const [selected, setSelected] = useState<IPaymentDateSelectorType>({ isPayNow: isSelected })

  const availability = AvailabilityBasic.fromJSONOrUndefined(availabilityJSON)
  if (!availability) return null

  const payDate = availability.getCancellationPolicyFromDateFormatted(i18n.locale)

  if (!availability.hasPayNow() || !availability.hasCancellationPolicy() || !payDate) return null

  const totalPrice = availability.getPriceFormatted(i18n.locale, 'total')
  const payNowPrice = availability.getPriceFormatted(i18n.locale, 'payNow')

  const handleClick = (value: IPaymentDateSelectorType) => {
    setSelected(value)
    onClick?.(value)
  }

  return (
    <div className={styles.paymentDateSelector}>
      <Typography as="h6" variant="title" size="xs" ellipsis>
        {i18n.formatMessage({ id: 'payment-date-selector.title' })}
      </Typography>

      <div className={styles.options}>
        <SelectableOption
          title={i18n.formatMessage({ id: 'policies.pay-on' }, { date: payDate })}
          value="default"
          onClick={() => handleClick({ isPayNow: false, availabilityId: availability.id })}
          variant={!selected.isPayNow ? 'selected' : 'default'}
        >
          <div className={styles.priceWrapper}>
            <Typography variant="caption" size="l" bold className="mr-8">
              {i18n.formatMessage({ id: 'global.you-pay' })}
            </Typography>
            <Typography variant="title" size="xs" bold>
              {totalPrice}
            </Typography>
          </div>
        </SelectableOption>

        <div className={styles.optionWrapper}>
          <SelectableOption
            title={i18n.formatMessage({ id: 'payment-date-selector.pay-now' })}
            value="now"
            onClick={() => handleClick({ isPayNow: true, availabilityId: availability.id })}
            variant={selected.isPayNow ? 'selected' : 'default'}
          >
            <div className={styles.tagWrapper}>
              <Tag size="m">{i18n.formatMessage({ id: 'policies.best-deal' })}</Tag>
            </div>
            <div className={styles.priceWrapper}>
              <Typography variant="caption" size="l" bold className="mr-8">
                {i18n.formatMessage({ id: 'global.you-pay' })}
              </Typography>
              <Typography variant="body" size="l" strike className="mr-4">
                {totalPrice}
              </Typography>
              <Typography variant="title" size="xs" bold color="var(--color-success-500)">
                {payNowPrice}
              </Typography>
            </div>
          </SelectableOption>
        </div>
      </div>

      <IconText size="xl" icon={<IconCheck />} color="var(--color-success-500)" wrap>
        {i18n.formatMessage({ id: 'payment-date-selector.free-cancellation' }, { date: payDate })}
      </IconText>
    </div>
  )
}

PaymentDateSelector.displayName = 'PaymentDateSelector'
export default withErrorBoundary(PaymentDateSelector)
